import { axiosConfig } from "../../../../axios/axiosConfig";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  convertDateFormat_DDMMYYYY,
  convertDateTimeFormat_DDMMYYYY_time,
} from "../../../../utils/common/dateConverter";

const headCellSx = {
  fontWeight: 700,
  fontSize: "12px",
  color: "#fff",
  background: "#04343a",
  borderBottom: "none",
  padding: "10px 14px",
  whiteSpace: "nowrap",
};

const AdBannersTable = ({ allBannersList, setAllBannersList }) => {
  const deleteBanner = async (bannerId) => {
    const response = await axiosConfig({
      method: "delete",
      url: "advertisement/screens/banner",
      data: { id: bannerId },
    });
    return response.data;
  };

  const deleteBannerMutation = useMutation({
    mutationFn: deleteBanner,
    onSuccess: (_, bannerId) => {
      setAllBannersList((prev) => prev.filter((banner) => banner.id !== bannerId));
    },
    onError: (error) => {
      console.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  const handleDeleteBanner = (bannerId) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      deleteBannerMutation.mutate(bannerId);
    }
  };

  const latestTimestampByScreen = allBannersList.reduce((acc, banner) => {
    const currentTime = new Date(banner.created_at).getTime();
    if (!acc[banner.screen_name] || currentTime > acc[banner.screen_name]) {
      acc[banner.screen_name] = currentTime;
    }
    return acc;
  }, {});

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        overflow: "hidden",
        mb: 3,
      }}
    >
      {/* Section header */}
      <Box sx={{ background: "#04343a", px: "20px", py: "12px" }}>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
          Banners Table
        </Typography>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ borderTop: "1px solid #eef2f7", overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: 800,
            "& .MuiTableCell-root": {
              padding: "9px 14px",
              fontSize: "12px",
              borderBottom: "1px solid #f0f4f8",
              whiteSpace: "nowrap",
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover": {
              background: "#f0faf5",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 45 }}>S.No.</TableCell>
              <TableCell sx={{ ...headCellSx, width: 120 }}>Screen</TableCell>
              <TableCell sx={{ ...headCellSx, width: 150 }}>Created On</TableCell>
              <TableCell sx={{ ...headCellSx, width: 100 }}>Start</TableCell>
              <TableCell sx={{ ...headCellSx, width: 100 }}>End</TableCell>
              <TableCell sx={{ ...headCellSx, width: 100 }}>Plan Code</TableCell>
              <TableCell sx={{ ...headCellSx, width: 80 }}>Deeplink</TableCell>
              <TableCell sx={{ ...headCellSx, width: 100 }}>Preview</TableCell>
              <TableCell sx={{ ...headCellSx, width: 80 }}>Latest</TableCell>
              <TableCell sx={{ ...headCellSx, width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!allBannersList.length ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 5, color: "#94a3b8", fontSize: "13px" }}>
                  No banners found.
                </TableCell>
              </TableRow>
            ) : (
              allBannersList.map(({ id, screen_name, created_at, from_date, till_date, screen_url, plan_code, deeplink }, index) => {
                const isLatest = new Date(created_at).getTime() === latestTimestampByScreen[screen_name];
                return (
                  <TableRow key={id}>
                    <TableCell sx={{ color: "#94a3b8" }}>{index + 1}</TableCell>
                    <TableCell sx={{ color: "#1e293b", fontWeight: 600 }}>{screen_name}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{convertDateTimeFormat_DDMMYYYY_time(created_at)}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{convertDateFormat_DDMMYYYY(from_date)}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{convertDateFormat_DDMMYYYY(till_date)}</TableCell>
                    <TableCell sx={{ color: "#64748b" }}>{plan_code || "—"}</TableCell>
                    <TableCell>
                      {deeplink ? (
                        <Tooltip title={deeplink} placement="top" arrow>
                          <a
                            href={deeplink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#016551", fontWeight: 600, textDecoration: "none", fontSize: "12px" }}
                          >
                            View
                          </a>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ color: "#94a3b8", fontSize: "12px" }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={<img src={screen_url} alt="Banner Preview" style={{ width: 250 }} />}
                        placement="top"
                      >
                        <IconButton size="small" sx={{ color: "#016551", "&:hover": { bgcolor: "#e6f4f1" } }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={isLatest ? "Latest" : "Old"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "11px",
                          bgcolor: isLatest ? "#dcfce7" : "#f1f5f9",
                          color: isLatest ? "#15803d" : "#64748b",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteBanner(id)}
                          sx={{ color: "#b91c1c", "&:hover": { bgcolor: "#fee2e2" } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdBannersTable;
